import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, Eye, Globe, Mail, Phone ,Instagram,Facebook,Youtube,Twitter,Linkedin} from 'lucide-react';
import { PageHeader } from '@/components/flup';
import { useToast } from '@/hooks/use-toast';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks'

import {createwebsite,getWebsiteByVendor} from '@/store/thunkMidddleware/turfMiddleware'
import { ButtonWithLoader } from '@/components/ui/buttonwithloader';
import CreateWebsiteSkeleton from '@/components/skeletons/CreateWebsiteSkeleton';
import MyWebsiteStatusComponent, { WebsiteStatus } from '@/components/website/WebsiteStatus';

const MyWebsite = () => {
    const dispatch= useAppDispatch()
    const [websiteStatus, setWebsiteStatus] = useState<WebsiteStatus>('pending');
    const [showForm, setShowForm] = useState(false);
    const {createwebsiteResponse,getWebsiteByVendorResponse} = useAppSelector(state=>state.turf)
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    subdomain: '',
    venueName: '',
    supportEmail: '',
    supportPhone: '',
    logo:null as File | null | string,
    dashboardImage: null as File | null | string,

    instagram:'' as string,
    facebook:'' as string,
    twitter:'' as string,
    youtube:'' as string,
    linkedin:'' as string,


  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageLogoPreview, setImageLogoPreview] = useState<string | null>(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateSubdomain = (value: string) => {
    const subdomainRegex = /^[a-z0-9-]+$/;
    if (!value) return 'Subdomain is required';
    if (value.length < 3) return 'Subdomain must be at least 3 characters';
    if (value.length > 30) return 'Subdomain must be less than 30 characters';
    if (!subdomainRegex.test(value)) return 'Only lowercase letters, numbers, and hyphens allowed';
    if (value.startsWith('-') || value.endsWith('-')) return 'Subdomain cannot start or end with hyphen';
    return '';
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Real-time subdomain validation
    if (field === 'subdomain') {
      const error = validateSubdomain(value);
      if (error) {
        setErrors(prev => ({ ...prev, subdomain: error }));
      }
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'Image size must be less than 5MB' }));
        return;
      }
      
      setFormData(prev => ({ ...prev, dashboardImage: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      // Clear error
      setErrors(prev => ({ ...prev, image: '' }));
    }
  };


  const handleLogoImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'Image size must be less than 5MB' }));
        return;
      }
      setFormData(prev => ({ ...prev, logo: file }));
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      // Clear error
      setErrors(prev => ({ ...prev, image: '' }));
    }
  };
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    
    const subdomainError = validateSubdomain(formData.subdomain);
    if (subdomainError) newErrors.subdomain = subdomainError;
    if(!formData.logo) newErrors.logo = 'Venue logo is required';
    if(!formData.dashboardImage) newErrors.dashboardImage = 'Venue dashboard image is required';
    if (!formData.venueName.trim()) newErrors.venueName = 'Venue name is required';
    if (!formData.supportEmail.trim()) newErrors.supportEmail = 'Support email is required';
    if (!/^\S+@\S+\.\S+$/.test(formData.supportEmail)) newErrors.supportEmail = 'Invalid email format';
    if (!formData.supportPhone.trim()) newErrors.supportPhone = 'Support phone is required';
    if (!/^\+?\d{10,15}$/.test(formData.supportPhone.replace(/\s/g, ''))) {
      newErrors.supportPhone = 'Invalid phone number format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    
    // TODO: Save to database
    console.log('Saving website data:', formData);

        dispatch(createwebsite({payload:formData,callback:(res)=>{
            console.log("createwebsite res:",res)
            toast({
              title: "Website Saved!",
              description: `Your website is now available at ${formData.subdomain}.bookyourzone.com`,
            });
            let {isWebsiteActive,isWebsiteDetailsCompleted} =res?.payload
            if(isWebsiteDetailsCompleted && isWebsiteActive){
              setWebsiteStatus('live')
              setShowForm(false)
            }else if(isWebsiteDetailsCompleted && !isWebsiteActive){
              setWebsiteStatus('in-progress')
              setShowForm(false)
            }else{
              setWebsiteStatus('pending')
              setShowForm(true)
            }
        }}))    

  };

  const handlePreview = () => {
    if (!formData.subdomain) {
      toast({
        title: "Error",
        description: "Please enter a subdomain first",
        variant: "destructive"
      });
      return;
    }

    if(getWebsiteByVendorResponse?.payload?.isWebsiteActive){
      const url = `https://${formData.subdomain}.bookyourzone.com`;
      window.open(url, '_blank');
    }else{
      toast({
        title: "Error",
        description: "Website is not active yet!",
        variant: "destructive"
      });
      return;
    }
  };

  useEffect(()=>{
    dispatch(getWebsiteByVendor({callback:(res)=>{
        console.log("getWebsiteByVendor:",res)
        if(res?.payload){
            let {subdomain,supportEmail,supportPhone,vendor,venueName,dashboardImage,logo,isWebsiteActive,isWebsiteDetailsCompleted,socialMediaLink} =res?.payload
            let {facebook='',instagram='',youtube='',twitter='',linkedin=''} = socialMediaLink
            if(dashboardImage?.url){
              setImagePreview(dashboardImage?.url)
            }
            if(logo?.url){
              setImageLogoPreview(logo?.url)
            }

            if(isWebsiteDetailsCompleted && isWebsiteActive){
              setWebsiteStatus('live')
              setShowForm(false)
            }else if(isWebsiteDetailsCompleted && !isWebsiteActive){
              setWebsiteStatus('in-progress')
              setShowForm(false)
            }else{
              setWebsiteStatus('pending')
              setShowForm(true)
            }
         
            setFormData((prev)=>{
                return {
                    ...prev,
                    subdomain:subdomain,
                    supportEmail,supportPhone,venueName,
                    dashboardImage:dashboardImage?.url,
                    logo:logo?.url,
                    facebook:facebook,
                    instagram,twitter,youtube,linkedin

                }
            })
        }
    }}))
  },[])

  if(getWebsiteByVendorResponse.isLoading){
    return <CreateWebsiteSkeleton/>
  }

  return (
    <div>
      <div className="max-w-4xl space-y-4">
        <PageHeader
          title="My website"
          subtitle="Your own booking page, on a bookyourzone.com address you choose."
        />

        <div className="grid gap-6">
          {/* Subdomain Section */}

           {/* Website Status Component */}
           <MyWebsiteStatusComponent 
            status={websiteStatus}
            subdomain={formData.subdomain}
            onEdit={() => setShowForm(true)}
          />

{(showForm || websiteStatus === 'pending') && (
            <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Your Booking URL
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="subdomain">Subdomain</Label>
                <div className="flex items-center mt-1">
                  <Input
                    id="subdomain"
                    disabled={!!getWebsiteByVendorResponse?.payload?.subdomain}
                    value={formData.subdomain}
                    onChange={(e) => handleInputChange('subdomain', e.target.value.toLowerCase())}
                    placeholder="myturf"
                    className={`rounded-r-none ${errors.subdomain ? 'border-destructive' : ''}`}
                  />
                  <div className="bg-muted px-3 py-2 border border-l-0 rounded-r-md text-muted-foreground">
                    .bookyourzone.com
                  </div>
                </div>
                {errors.subdomain && (
                  <p className="text-destructive text-sm mt-1">{errors.subdomain}</p>
                )}
                {formData.subdomain && !errors.subdomain && (
                  <p className="text-primary text-sm mt-1">
                    ✓ Your website will be: {formData.subdomain}.bookyourzone.com
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Venue Details */}
          <Card>
            <CardHeader>
              <CardTitle>Venue Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="venueName" className='mb-2'>Venue Name</Label>
                <Input
                  id="venueName"
                  value={formData.venueName}
                  onChange={(e) => handleInputChange('venueName', e.target.value)}
                  placeholder="My Turf / Mansow Sports / etc."
                  className={errors.venueName ? 'border-destructive' : ''}
                />
                {errors.venueName && (
                  <p className="text-destructive text-sm mt-1">{errors.venueName}</p>
                )}
              </div>

  {/* Logo Image Upload */}
          <div>
                <Label>Upload Your Logo* (Max Size 2MB,Tranparent Background)</Label>
                <div className="mt-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label htmlFor="LogoimageUpload" className="cursor-pointer">
                        <div className="border-2 border-dashed border-input rounded-lg p-6 text-center hover:border-primary transition-colors">
                          <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            Click to upload image (max 2MB)
                          </p>
                        </div>
                      </label>
                      <input
                        id="LogoimageUpload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoImageUpload}
                        className="hidden"
                      />
                    </div>
                    
                    {imageLogoPreview && (
                      <div className="w-32 h-32 border rounded-lg overflow-hidden">
                        <img
                          src={imageLogoPreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                  {errors.logo && (
                    <p className="text-destructive text-sm mt-1">{errors.logo}</p>
                  )}
                </div>
              </div>

              {/* Dashboard Image Upload */}
              <div>
                <Label>Dashboard Image</Label>
                <div className="mt-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label htmlFor="DashboardimageUpload" className="cursor-pointer">
                        <div className="border-2 border-dashed border-input rounded-lg p-6 text-center hover:border-primary transition-colors">
                          <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            Click to upload image (max 5MB)
                          </p>
                        </div>
                      </label>
                      <input
                        id="DashboardimageUpload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </div>
                    
                    {imagePreview && (
                      <div className="w-32 h-32 border rounded-lg overflow-hidden">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                  {errors.dashboardImage && (
                    <p className="text-destructive text-sm mt-1">{errors.dashboardImage}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>


          {/* Social media link Details */}
          <Card>
            <CardHeader>
              <CardTitle>Social Media Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="instagram" className="flex items-center gap-2 mb-2">
                  <Instagram className="h-4 w-4" />
                  Instagram
                </Label>
                <Input
                  id="instagram"
                  type="text"
                  value={formData.instagram}
                  onChange={(e) => handleInputChange('instagram', e.target.value)}
                  placeholder="https://www.instagram.com/bookyourzone.official/"
                  className={errors.instagram ? 'border-destructive' : ''}
                />
                {errors?.instagram && (
                  <p className="text-destructive text-sm mt-1">{errors.instagram}</p>
                )}
              </div>

              <div>
                <Label htmlFor="facebook" className="flex items-center gap-2 mb-2">
                  <Facebook className="h-4 w-4" />
                  Facebook
                </Label>
                <Input
                  id="facebook"
                  type="text"
                  value={formData.facebook}
                  onChange={(e) => handleInputChange('facebook', e.target.value)}
                  placeholder="https://www.instagram.com/bookyourzone.official/"
                  className={errors.facebook ? 'border-destructive' : ''}
                />
                {errors?.facebook && (
                  <p className="text-destructive text-sm mt-1">{errors.facebook}</p>
                )}
              </div>

              <div>
                <Label htmlFor="linkedin" className="flex items-center gap-2 mb-2">
                  <Linkedin className="h-4 w-4" />
                  Linkedin
                </Label>
                <Input
                  id="linkedin"
                  type="text"
                  value={formData.linkedin}
                  onChange={(e) => handleInputChange('linkedin', e.target.value)}
                  placeholder="https://www.linkedin.com/company/bookyourzone"
                  className={errors.linkedin ? 'border-destructive' : ''}
                />
                {errors?.linkedin && (
                  <p className="text-destructive text-sm mt-1">{errors.linkedin}</p>
                )}
              </div>

              <div>
                <Label htmlFor="twitter" className="flex items-center gap-2 mb-2">
                  <Twitter className="h-4 w-4" />
                  Twitter
                </Label>
                <Input
                  id="twitter"
                  type="text"
                  value={formData.twitter}
                  onChange={(e) => handleInputChange('twitter', e.target.value)}
                  placeholder="https://x.com/bookyourzone"
                  className={errors.twitter ? 'border-destructive' : ''}
                />
                {errors?.twitter && (
                  <p className="text-destructive text-sm mt-1">{errors.twitter}</p>
                )}
              </div>

              <div>
                <Label htmlFor="youtube" className="flex items-center gap-2 mb-2">
                  <Youtube className="h-4 w-4" />
                  Youtube
                </Label>
                <Input
                  id="youtube"
                  type="text"
                  value={formData.youtube}
                  onChange={(e) => handleInputChange('youtube', e.target.value)}
                  placeholder="https://www.instagram.com/bookyourzone.official/"
                  className={errors.youtube ? 'border-destructive' : ''}
                />
                {errors?.youtube && (
                  <p className="text-destructive text-sm mt-1">{errors.youtube}</p>
                )}
              </div>

         
            </CardContent>
          </Card>


          {/* Support Contact Details */}
          <Card>
            <CardHeader>
              <CardTitle>Support Contact Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="supportEmail" className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4" />
                  Support Email
                </Label>
                <Input
                  id="supportEmail"
                  type="email"
                  value={formData.supportEmail}
                  onChange={(e) => handleInputChange('supportEmail', e.target.value)}
                  placeholder="support@example.com"
                  className={errors.supportEmail ? 'border-destructive' : ''}
                />
                {errors.supportEmail && (
                  <p className="text-destructive text-sm mt-1">{errors.supportEmail}</p>
                )}
              </div>

              <div>
                <Label htmlFor="supportPhone" className="flex items-center gap-2 mb-2">
                  <Phone className="h-4 w-4" />
                  Support Phone
                </Label>
                <Input
                  id="supportPhone"
                  type="tel"
                  value={formData.supportPhone}
                  onChange={(e) => handleInputChange('supportPhone', e.target.value)}
                  placeholder="+91 9876543210"
                  className={errors.supportPhone ? 'border-destructive' : ''}
                />
                {errors.supportPhone && (
                  <p className="text-destructive text-sm mt-1">{errors.supportPhone}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center">
            <ButtonWithLoader
            loaderPosition='right'
             isLoading={createwebsiteResponse.isLoading}
             disabled ={createwebsiteResponse.isLoading}
              onClick={handleSave} className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Save Website
            </ButtonWithLoader>
            <Button 
              variant="outline" 
              onClick={handlePreview}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Preview Link
            </Button>
          </div>
        </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default MyWebsite;